import React, { useMemo, useState } from "react";
import { format, isValid } from "date-fns";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clientBackendPDF } from "configurations/axios";
import { updateEstadoContable } from "redux/actions/estadoContableActions";
import TablePaginationActions from "widgets/Tables/Pagination";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { es } from "date-fns/locale";
import SelectorMesAnio from "../components/SelectorMesAnio";
import DarkDatePicker from "../components/DarkDatePicker";
import useContableDashboard from "../hooks/useContableDashboard";

const ESTADO_PAGADO = "PAGADO";
const ESTADO_POR_COBRAR = "POR_COBRAR";

const useStyles = makeStyles((theme) => ({
  root: {
    boxSizing: "border-box",
    minHeight: "calc(100vh - 96px)",
    background: "#080f1f",
    color: "#f5f7fb",
    padding: theme.spacing(3, 3, 9),
  },
  header: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  title: {
    color: "#f5f7fb",
    fontSize: 19,
    fontWeight: 900,
  },
  actions: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
  },
  search: {
    width: 280,
    "& .MuiInputBase-root": {
      background: "#111a2b",
      borderRadius: 6,
      color: "#b8cdf0",
      fontSize: 13,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1d3454",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2b4a73",
    },
    "& .MuiInputLabel-root": {
      color: "#8297b6",
      fontSize: 13,
    },
  },
  newButton: {
    background: "linear-gradient(135deg, #02dfb1 0%, #19b8f2 100%)",
    borderRadius: 8,
    color: "#06101f",
    fontSize: 12,
    fontWeight: 900,
    padding: theme.spacing(1, 1.8),
    textTransform: "none",
    "&:hover": {
      background: "linear-gradient(135deg, #02caa2 0%, #139ed2 100%)",
    },
  },
  tabs: {
    alignItems: "center",
    background: "#111a2b",
    borderRadius: 8,
    display: "inline-flex",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2.5),
    padding: 4,
  },
  tab: {
    borderRadius: 6,
    color: "#8fb1d8",
    fontSize: 12,
    fontWeight: 700,
    padding: theme.spacing(1, 2),
    textTransform: "none",
  },
  activeTab: {
    background: "#0d172b",
    color: "#ffffff",
  },
  panel: {
    background: "#111a2b",
    border: "1px solid #1d3454",
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    overflow: "hidden",
  },
  advancedPanel: {
    background: "#111a2b",
    border: "1px solid #1d3454",
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    overflow: "hidden",
  },
  advancedToggle: {
    alignItems: "center",
    cursor: "pointer",
    display: "flex",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    userSelect: "none",
  },
  advancedToggleLabel: {
    color: "#8fb1d8",
    fontSize: 13,
    fontWeight: 700,
  },
  advancedToggleLink: {
    color: "#19b8f2",
    fontSize: 12,
    fontWeight: 700,
  },
  advancedChevron: {
    color: "#8fb1d8",
    marginLeft: "auto",
    transition: "transform 0.15s ease",
  },
  advancedChevronOpen: {
    transform: "rotate(180deg)",
  },
  advancedBody: {
    borderTop: "1px solid #1d3454",
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  advancedField: {
    minWidth: 180,
    "& .MuiInputBase-root": {
      background: "#0d172b",
      borderRadius: 6,
      color: "#f5f7fb",
      fontSize: 13,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1d3454",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2b4a73",
    },
    "& .MuiInputLabel-root": {
      color: "#f5f7fb",
      fontSize: 13,
    },
    "& .MuiSvgIcon-root": {
      color: "#8fb1d8",
    },
  },
  advancedActions: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1.5),
  },
  advancedSearchButton: {
    background: "linear-gradient(135deg, #02dfb1 0%, #19b8f2 100%)",
    borderRadius: 8,
    color: "#06101f",
    fontSize: 12,
    fontWeight: 900,
    padding: theme.spacing(1, 2.2),
    textTransform: "none",
    "&:hover": {
      background: "linear-gradient(135deg, #02caa2 0%, #139ed2 100%)",
    },
  },
  advancedClearButton: {
    color: "#8fb1d8",
    fontSize: 12,
    fontWeight: 700,
    padding: theme.spacing(1, 1.5),
    textTransform: "none",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  table: {
    borderCollapse: "collapse",
    minWidth: 1120,
    width: "100%",
  },
  tableHead: {
    background: "#1a253b",
  },
  headerCell: {
    color: "#53647e",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1,
    padding: theme.spacing(1.35, 2),
    textAlign: "left",
    textTransform: "uppercase",
  },
  bodyRow: {
    borderTop: "1px solid #1d3454",
  },
  bodyCell: {
    color: "#acd0f6",
    fontSize: 13,
    padding: theme.spacing(1.7, 2),
  },
  strongCell: {
    color: "#bcd8f8",
    fontSize: 13,
    fontWeight: 900,
    padding: theme.spacing(1.7, 2),
    whiteSpace: "nowrap",
  },
  totalCell: {
    color: "#bcd8f8",
    fontSize: 13,
    fontWeight: 900,
    padding: theme.spacing(1.7, 2),
    whiteSpace: "nowrap",
  },
  status: {
    borderRadius: 999,
    display: "inline-flex",
    fontSize: 11,
    fontWeight: 900,
    padding: theme.spacing(0.4, 1),
  },
  paid: {
    background: "#073f3f",
    color: "#02dfb1",
  },
  pending: {
    background: "#3a2807",
    color: "#ffb229",
  },
  viewButton: {
    color: "#8bbcff",
    minWidth: 0,
    padding: theme.spacing(0.5),
  },
  statusText: {
    color: "#b8cdf0",
    fontSize: 13,
    padding: theme.spacing(2),
  },
  errorText: {
    color: "#ff8a9a",
    fontSize: 13,
    padding: theme.spacing(2),
  },
  pagination: {
    alignItems: "center",
    background: "#111a2b",
    borderTop: "1px solid #1d3454",
    color: "#acd0f6",
    display: "flex",
    justifyContent: "flex-end",
    minHeight: 52,
    "& .MuiTablePagination-toolbar": {
      minHeight: 52,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
    },
    "& .MuiTablePagination-caption": {
      color: "#acd0f6",
      fontSize: 12,
    },
    "& .MuiTablePagination-selectIcon": {
      color: "#acd0f6",
    },
    "& .MuiSelect-root": {
      color: "#f5f7fb",
      fontSize: 12,
    },
  },
  dialogPaper: {
    background: "#080f1f",
    color: "#f5f7fb",
  },
  dialogTitle: {
    background: "#0d172b",
    borderBottom: "1px solid #1d3454",
    padding: theme.spacing(2, 3),
  },
  dialogHeader: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "space-between",
  },
  dialogSubtitle: {
    color: "#8fb1d8",
    fontSize: 12,
    marginTop: theme.spacing(0.5),
  },
  dialogContent: {
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "280px minmax(0, 1fr)",
    minHeight: "72vh",
    padding: theme.spacing(2),
  },
  detailPanel: {
    background: "#111a2b",
    border: "1px solid #1d3454",
    borderRadius: 8,
    padding: theme.spacing(2),
  },
  detailLabel: {
    color: "#53647e",
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: 1,
    marginTop: theme.spacing(1.5),
    textTransform: "uppercase",
  },
  detailValue: {
    color: "#acd0f6",
    fontSize: 13,
    wordBreak: "break-word",
  },
  selectControl: {
    marginTop: theme.spacing(2),
    width: "100%",
    "& .MuiInputLabel-root": {
      color: "#8fb1d8",
    },
    "& .MuiOutlinedInput-root": {
      color: "#f5f7fb",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1d3454",
    },
    "& .MuiSvgIcon-root": {
      color: "#acd0f6",
    },
  },
  datePicker: {
    marginTop: theme.spacing(2),
    width: "100%",
    "& .MuiInputLabel-root": {
      color: "#8fb1d8",
    },
    "& .MuiOutlinedInput-root": {
      color: "#f5f7fb",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1d3454",
    },
    "& .MuiSvgIcon-root": {
      color: "#acd0f6",
    },
  },
  ridePanel: {
    background: "#111a2b",
    border: "1px solid #1d3454",
    borderRadius: 8,
    minHeight: "72vh",
    overflow: "hidden",
    position: "relative",
  },
  rideFrame: {
    background: "#ffffff",
    border: 0,
    height: "100%",
    minHeight: "72vh",
    width: "100%",
  },
  rideLoader: {
    alignItems: "center",
    color: "#acd0f6",
    display: "flex",
    gap: theme.spacing(1),
    height: "100%",
    justifyContent: "center",
    minHeight: "72vh",
  },
  dialogActions: {
    background: "#0d172b",
    borderTop: "1px solid #1d3454",
    padding: theme.spacing(1.5, 3),
  },
  saveButton: {
    background: "linear-gradient(135deg, #02dfb1 0%, #19b8f2 100%)",
    color: "#06101f",
    fontWeight: 900,
    textTransform: "none",
    "&:hover": {
      background: "linear-gradient(135deg, #02caa2 0%, #139ed2 100%)",
    },
  },
  closeButton: {
    color: "#acd0f6",
    textTransform: "none",
  },
}));

const getEstadoContable = (voucher) =>
  voucher?.comprobanteElectronico?.estadoContable || ESTADO_POR_COBRAR;

const getVoucherNumber = (voucher) => {
  const comprobante = voucher.comprobanteElectronico || {};

  return [
    comprobante.codigoSucursal,
    comprobante.puntoFacturacion,
    comprobante.numeroFactura,
  ]
    .filter(Boolean)
    .join("-");
};

const formatDate = (date) => {
  if (!date) return "";

  return new Date(date).toLocaleDateString("es-EC", {
    day: "2-digit",
    month: "short",
  });
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") return "$0.00";

  return `$${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const normalizeText = (value) => `${value || ""}`.toLowerCase();

const FacturasView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const tokenUser = useSelector((state) => state.user.tk);
  const {
    data: statsData,
    authorizedVouchers,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    reload,
    mes,
    anio,
    onMesChange,
    onAnioChange,
    setAdvancedFilters,
    clearAdvancedFilters,
  } = useContableDashboard({ voucherType: "01", fetchStats: true, useMonthFilter: true });
  const [selectedStatus, setSelectedStatus] = useState("TODAS");
  const [search, setSearch] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [estadoDraft, setEstadoDraft] = useState(ESTADO_POR_COBRAR);
  const [fechaPago, setFechaPago] = useState(null);
  const [rideUrl, setRideUrl] = useState("");
  const [rideLoading, setRideLoading] = useState(false);

  // Busqueda avanzada (panel colapsable en Facturas): inicia oculto.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [fechaInicioDraft, setFechaInicioDraft] = useState(null);
  const [fechaFinDraft, setFechaFinDraft] = useState(null);
  const [secuencialDraft, setSecuencialDraft] = useState("");
  const [parametroReceptorDraft, setParametroReceptorDraft] = useState("");

  const rows = authorizedVouchers.comprobantes || [];

  // Conteos reales del mes (no solo de la pagina cargada). Usa el resumen
  // del dashboard (totalFacturas / porCobrarCantidad), que el backend ya
  // calcula sobre TODO el mes filtrado. Mientras ese resumen no ha llegado
  // todavia, cae al conteo de la pagina actual como aproximacion temporal.
  const counts = useMemo(() => {
    if (statsData) {
      const totalFacturas = statsData.totalFacturas || 0;
      const porCobrar = statsData.porCobrarCantidad || 0;

      return {
        TODAS: totalFacturas,
        [ESTADO_PAGADO]: Math.max(totalFacturas - porCobrar, 0),
        [ESTADO_POR_COBRAR]: porCobrar,
      };
    }

    return {
      TODAS: authorizedVouchers.totalElements || rows.length,
      [ESTADO_PAGADO]: rows.filter(
        (voucher) => getEstadoContable(voucher) === ESTADO_PAGADO,
      ).length,
      [ESTADO_POR_COBRAR]: rows.filter(
        (voucher) => getEstadoContable(voucher) === ESTADO_POR_COBRAR,
      ).length,
    };
  }, [statsData, authorizedVouchers.totalElements, rows]);

  const filteredRows = useMemo(() => {
    const query = normalizeText(search);

    return rows.filter((voucher) => {
      const comprobante = voucher.comprobanteElectronico || {};
      const receptor = voucher.receptor || {};
      const estado = getEstadoContable(voucher);
      const matchesStatus =
        selectedStatus === "TODAS" || estado === selectedStatus;
      const matchesSearch =
        !query ||
        normalizeText(getVoucherNumber(voucher)).includes(query) ||
        normalizeText(receptor.razonSocial).includes(query) ||
        normalizeText(receptor.identificacionReceptor).includes(query) ||
        normalizeText(comprobante.cufe).includes(query);

      return matchesStatus && matchesSearch;
    });
  }, [rows, search, selectedStatus]);

  const openDetail = async (voucher) => {
    const comprobante = voucher.comprobanteElectronico || {};

    setSelectedVoucher(voucher);
    setEstadoDraft(getEstadoContable(voucher));
    setFechaPago(voucher?.comprobanteElectronico?.fechaPago ? new Date(voucher.comprobanteElectronico.fechaPago) : null);
    setDetailOpen(true);
    setRideLoading(true);

    if (rideUrl) {
      window.URL.revokeObjectURL(rideUrl);
      setRideUrl("");
    }

    try {
      const response = await clientBackendPDF.get(
        `/utilidades/generar/ride/${comprobante.idComprobanteElectronico}`,
      );
      const file = new Blob([response.data], { type: "application/pdf" });
      setRideUrl(window.URL.createObjectURL(file));
    } catch (error) {
      enqueueSnackbar("No se pudo cargar el RIDE de la factura.", {
        variant: "error",
      });
    } finally {
      setRideLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedVoucher(null);

    if (rideUrl) {
      window.URL.revokeObjectURL(rideUrl);
      setRideUrl("");
    }
  };

  const saveEstadoDetail = async () => {
    if (estadoDraft === ESTADO_PAGADO && (!fechaPago || !isValid(fechaPago))) {
      enqueueSnackbar("Debes seleccionar la fecha de pago.", {
        variant: "warning",
      });
      return;
    }

    const idFactura =
      selectedVoucher?.comprobanteElectronico?.idComprobanteElectronico;
    const fechaPagoStr = estadoDraft === ESTADO_PAGADO && fechaPago
      ? format(fechaPago, "yyyy-MM-dd")
      : null;

    try {
      setUpdatingId(idFactura);
      await dispatch(updateEstadoContable(idFactura, estadoDraft, fechaPagoStr));
      enqueueSnackbar("Estado contable actualizado.", { variant: "success" });
      closeDetail();
      reload();
    } catch (error) {
      enqueueSnackbar("No se pudo actualizar el estado contable.", {
        variant: "error",
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleBuscarAvanzada = () => {
    setAdvancedFilters({
      fechaInicio:
        fechaInicioDraft && isValid(fechaInicioDraft)
          ? format(fechaInicioDraft, "yyyy-MM-dd")
          : null,
      fechaFin:
        fechaFinDraft && isValid(fechaFinDraft)
          ? format(fechaFinDraft, "yyyy-MM-dd")
          : null,
      secuencial: secuencialDraft.trim() || null,
      parametroReceptor: parametroReceptorDraft.trim() || null,
    });
  };

  const handleLimpiarAvanzada = () => {
    setFechaInicioDraft(null);
    setFechaFinDraft(null);
    setSecuencialDraft("");
    setParametroReceptorDraft("");
    clearAdvancedFilters();
  };

  const tabs = [
    { label: "Todas", value: "TODAS" },
    { label: "Pagadas", value: ESTADO_PAGADO },
    { label: "Por cobrar", value: ESTADO_POR_COBRAR },
  ];

  return (
    <div className={classes.root}>
      <SelectorMesAnio
        mes={mes}
        anio={anio}
        onMesChange={onMesChange}
        onAnioChange={onAnioChange}
      />

      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={es}>
        <div className={classes.advancedPanel}>
          <div
            className={classes.advancedToggle}
            onClick={() => setAdvancedOpen((prev) => !prev)}
          >
            <Typography className={classes.advancedToggleLabel}>
              Busqueda avanzada
            </Typography>
            <Typography className={classes.advancedToggleLink}>
              (Clic Aqui)
            </Typography>
            <ExpandMoreIcon
              className={`${classes.advancedChevron} ${
                advancedOpen ? classes.advancedChevronOpen : ""
              }`}
            />
          </div>
          {advancedOpen && (
            <div className={classes.advancedBody}>
              <DarkDatePicker
                autoOk
                className={classes.advancedField}
                clearable
                format="dd/MM/yyyy"
                inputVariant="outlined"
                label="Fecha Inicial"
                onChange={setFechaInicioDraft}
                value={fechaInicioDraft}
              />
              <DarkDatePicker
                autoOk
                className={classes.advancedField}
                clearable
                format="dd/MM/yyyy"
                inputVariant="outlined"
                label="Fecha Final"
                onChange={setFechaFinDraft}
                value={fechaFinDraft}
              />
              <TextField
                className={classes.advancedField}
                label="Secuencial"
                onChange={(event) => setSecuencialDraft(event.target.value)}
                value={secuencialDraft}
                variant="outlined"
              />
              <TextField
                className={classes.advancedField}
                label="RUC / Cedula / Razon Social"
                onChange={(event) => setParametroReceptorDraft(event.target.value)}
                value={parametroReceptorDraft}
                variant="outlined"
              />
              <div className={classes.advancedActions}>
                <Button
                  className={classes.advancedSearchButton}
                  onClick={handleBuscarAvanzada}
                >
                  Buscar
                </Button>
                <Button
                  className={classes.advancedClearButton}
                  onClick={handleLimpiarAvanzada}
                >
                  Limpiar / Mostrar Todos
                </Button>
              </div>
            </div>
          )}
        </div>
      </MuiPickersUtilsProvider>

      <div className={classes.tabs}>
        {tabs.map((tab) => (
          <Button
            className={`${classes.tab} ${
              selectedStatus === tab.value ? classes.activeTab : ""
            }`}
            key={tab.value}
            onClick={() => setSelectedStatus(tab.value)}
          >
            {tab.label} ({counts[tab.value] || 0})
          </Button>
        ))}
      </div>

      <div className={classes.panel}>
        {authorizedVouchers.loading && (
          <Typography className={classes.statusText}>
            Cargando facturas...
          </Typography>
        )}
        {authorizedVouchers.error && (
          <Typography className={classes.errorText}>
            No se pudieron cargar las facturas.
          </Typography>
        )}
        <div className={classes.tableWrapper}>
          <table className={classes.table}>
            <thead className={classes.tableHead}>
              <tr>
                <th className={classes.headerCell}>N Factura</th>
                <th className={classes.headerCell}>Cliente</th>
                <th className={classes.headerCell}>Emision</th>
                <th className={classes.headerCell}>Subtotal</th>
                <th className={classes.headerCell}>ITBMS</th>
                <th className={classes.headerCell}>Total</th>
                <th className={classes.headerCell}>Estado</th>
                <th className={classes.headerCell}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((voucher) => {
                const comprobante = voucher.comprobanteElectronico || {};
                const receptor = voucher.receptor || {};
                const estado = getEstadoContable(voucher);
                const isPaid = estado === ESTADO_PAGADO;

                return (
                  <tr
                    className={classes.bodyRow}
                    key={voucher.idAsignacionComprobanteElectronico}
                  >
                    <td className={classes.strongCell}>
                      {getVoucherNumber(voucher)}
                    </td>
                    <td className={classes.bodyCell}>
                      {receptor.razonSocial || "Consumidor final"}
                    </td>
                    <td className={classes.bodyCell}>
                      {formatDate(comprobante.fechaEmision)}
                    </td>
                    <td className={classes.bodyCell}>
                      {formatCurrency(comprobante.valorSubtotal)}
                    </td>
                    <td className={classes.bodyCell}>
                      {formatCurrency(comprobante.valorItbms)}
                    </td>
                    <td className={classes.totalCell}>
                      {formatCurrency(comprobante.valorTotal)}
                    </td>
                    <td className={classes.bodyCell}>
                      <span
                        className={`${classes.status} ${
                          isPaid ? classes.paid : classes.pending
                        }`}
                      >
                        {isPaid ? "Pagado" : "Por cobrar"}
                      </span>
                    </td>
                    <td className={classes.bodyCell}>
                      <Tooltip title="Ver factura y cambiar estado">
                        <Button
                          className={classes.viewButton}
                          onClick={() => openDetail(voucher)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
              {filteredRows.length === 0 && !authorizedVouchers.loading && (
                <tr className={classes.bodyRow}>
                  <td className={classes.bodyCell} colSpan={8}>
                    No existen facturas para mostrar.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          ActionsComponent={TablePaginationActions}
          className={classes.pagination}
          component="div"
          count={authorizedVouchers.totalElements || 0}
          labelDisplayedRows={({ from, to, count }) =>
            count ? `${from}-${to} de ${count}` : "0 de 0"
          }
          labelRowsPerPage="Filas por pagina:"
          onChangePage={onPageChange}
          onChangeRowsPerPage={onRowsPerPageChange}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 15, 25]}
        />
      </div>

      <Dialog
        classes={{ paper: classes.dialogPaper }}
        fullWidth
        maxWidth="lg"
        onClose={closeDetail}
        open={detailOpen}
      >
        <DialogTitle className={classes.dialogTitle} disableTypography>
          <div className={classes.dialogHeader}>
            <div>
              <Typography className={classes.title}>
                Factura {selectedVoucher ? getVoucherNumber(selectedVoucher) : ""}
              </Typography>
              <Typography className={classes.dialogSubtitle}>
                Vista RIDE y estado contable
              </Typography>
            </div>
            <span
              className={`${classes.status} ${
                estadoDraft === ESTADO_PAGADO ? classes.paid : classes.pending
              }`}
            >
              {estadoDraft === ESTADO_PAGADO ? "Pagado" : "Por cobrar"}
            </span>
          </div>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <div className={classes.detailPanel}>
            <Typography className={classes.detailLabel}>Cliente</Typography>
            <Typography className={classes.detailValue}>
              {selectedVoucher?.receptor?.razonSocial || "Consumidor final"}
            </Typography>

            <Typography className={classes.detailLabel}>CUFE</Typography>
            <Typography className={classes.detailValue}>
              {selectedVoucher?.comprobanteElectronico?.cufe || ""}
            </Typography>

            <Typography className={classes.detailLabel}>Emision</Typography>
            <Typography className={classes.detailValue}>
              {formatDate(selectedVoucher?.comprobanteElectronico?.fechaEmision)}
            </Typography>

            <Typography className={classes.detailLabel}>Total</Typography>
            <Typography className={classes.detailValue}>
              {formatCurrency(selectedVoucher?.comprobanteElectronico?.valorTotal)}
            </Typography>

            <FormControl className={classes.selectControl} variant="outlined">
              <InputLabel id="estado-contable-label">Estado contable</InputLabel>
              <Select
                label="Estado contable"
                labelId="estado-contable-label"
                onChange={(event) => setEstadoDraft(event.target.value)}
                value={estadoDraft}
              >
                <MenuItem value={ESTADO_POR_COBRAR}>POR COBRAR</MenuItem>
                <MenuItem value={ESTADO_PAGADO}>PAGADO</MenuItem>
              </Select>
            </FormControl>

            {estadoDraft === ESTADO_PAGADO && (
              <MuiPickersUtilsProvider locale={es} utils={DateFnsUtils}>
                <DarkDatePicker
                  autoOk
                  className={classes.datePicker}
                  disableFuture
                  format="dd/MM/yyyy"
                  inputVariant="outlined"
                  label="Fecha de pago"
                  onChange={(date) => setFechaPago(date)}
                  value={fechaPago}
                />
              </MuiPickersUtilsProvider>
            )}
          </div>

          <div className={classes.ridePanel}>
            {rideLoading && (
              <div className={classes.rideLoader}>
                <CircularProgress color="inherit" size={24} />
                <span>Cargando RIDE...</span>
              </div>
            )}
            {!rideLoading && rideUrl && (
              <iframe
                className={classes.rideFrame}
                src={rideUrl}
                title="RIDE de factura"
              />
            )}
            {!rideLoading && !rideUrl && (
              <div className={classes.rideLoader}>
                No se pudo mostrar el RIDE.
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button className={classes.closeButton} onClick={closeDetail}>
            Cerrar
          </Button>
          <Button
            className={classes.saveButton}
            disabled={updatingId === selectedVoucher?.comprobanteElectronico?.idComprobanteElectronico}
            onClick={saveEstadoDetail}
            variant="contained"
          >
            Guardar estado
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FacturasView;